import { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetProgressObjectDashboardQuery, useListProgressObjectsQuery } from '@shared/api';
import {
  apiErrorMessage,
  fmtDateTime,
  suggestInstructionText,
  useProgressPermission,
  type InstructionTopic,
  type ProgressObjectItem,
} from '@entities/progress';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { popIn } from '@shared/theme/animations';
import { IconClipboardCheck, IconPencil, IconRefresh } from '@shared/ui/icons';
import {
  BillingCard,
  ClaimsCard,
  CorrespondenceCard,
  KspCard,
  OtherCard,
  ProgressCard,
} from './dashboard/blocks';
import { ChronumBanner } from './dashboard/ChronumBanner';
import { InstructionModal } from './dashboard/InstructionModal';
import { ObjectProfileModal } from './dashboard/ObjectProfileModal';
import { ObjectEconomyCard } from '../Economist/ObjectEconomyCard';
import {
  getActiveProjectId,
  setActiveProjectId,
  useActiveProjectContext,
} from '@shared/lib/activeProjectContext';

/* Главный экран ОСНОВА.Руководитель — дашборд объекта (ТЗ 09.10.2026).
   Выбрал объект → сразу все показатели: переписка с Заказчиком, претензии,
   прогресс по КФО, выполнено и запроцентовано, план/факт КСП, прочее. Без
   очереди и LLM — цифры собирает бэкенд из агентов-источников. */

/** Последний выбранный объект — единый сквозной контекст + fallback в localStorage. */
const LAST_OBJECT_KEY = 'osnova.progress.object';

function readLastObject(): string | null {
  const shared = getActiveProjectId();
  if (shared) return shared;
  try {
    return window.localStorage.getItem(LAST_OBJECT_KEY);
  } catch {
    return null;
  }
}

function writeLastObject(id: string) {
  setActiveProjectId(id);
  try {
    window.localStorage.setItem(LAST_OBJECT_KEY, id);
  } catch {
    /* приватный режим / запрет хранилища — просто не запоминаем */
  }
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
`;

const PickerLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ObjectChip = styled.button<{ $active: boolean; $index: number }>`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  font: inherit;
  text-align: left;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  animation: ${popIn} 300ms ${({ theme }) => theme.motion.spring} both;
  animation-delay: ${({ $index }) => `${$index * 40}ms`};
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  & > b {
    font-size: 13.5px;
    font-weight: 700;
  }
  & > small {
    font-size: 11.5px;
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
  &:focus-visible {
    outline: 2px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.colors.primary};
      background: ${theme.colors.primarySoft};
      box-shadow: ${theme.shadow.sm};
    `}
`;

const ObjectHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const ObjectTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const ObjectMeta = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const HeadButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};

  & svg {
    margin-right: ${({ theme }) => theme.spacing(1.5)};
    vertical-align: -2px;
  }
`;

const Warnings = styled.ul`
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)} ${theme.spacing(3)} ${theme.spacing(8)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 12.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* Четыре основные карточки — одной высоты: `grid-auto-rows: 1fr` выравнивает все
   ряды по самой высокой карточке, ссылка на источник прижата к низу карточки. На
   узком экране карточки идут столбиком и растягивать их незачем. */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  gap: ${({ theme }) => theme.spacing(4)};

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

/* Пока грузится другой объект, на экране остаются прежние карточки (приглушённые):
   страница не схлопывается в «Собираю…» и не проигрывает анимацию заново. */
const Content = styled.div<{ $stale: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  opacity: ${({ $stale }) => ($stale ? 0.45 : 1)};
  pointer-events: ${({ $stale }) => ($stale ? 'none' : 'auto')};
  transition: opacity ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
`;

const Empty = styled.div`
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => `${theme.spacing(6)} 0`};
`;

function pickInitial(objects: ProgressObjectItem[], fromUrl: string | null): string | null {
  if (fromUrl && objects.some((o) => o.id === fromUrl)) return fromUrl;
  const last = readLastObject();
  if (last && objects.some((o) => o.id === last)) return last;
  return objects.find((o) => o.status === 'active')?.id ?? objects[0]?.id ?? null;
}

/**
 * Корень `/agents/progress` — дашборд объекта. Объект выбирается чипами
 * (запоминается в `?object=` и у зрителя в браузере), ниже — блоки показателей.
 */
export function ProgressObjectDashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { activeProjectId } = useActiveProjectContext();
  const { can_read, can_write } = useProgressPermission();
  const objectsQ = useListProgressObjectsQuery(undefined, { skip: !can_read });
  const objects = useMemo(() => objectsQ.data ?? [], [objectsQ.data]);
  const urlObject = params.get('object');
  const selected = useMemo(
    () => pickInitial(objects, urlObject ?? activeProjectId),
    [objects, urlObject, activeProjectId],
  );
  const [profileOpen, setProfileOpen] = useState(false);
  // Блок, по которому руководитель выдаёт поручение (окно открыто).
  const [instructTopic, setInstructTopic] = useState<InstructionTopic | null>(null);

  // Выбор по умолчанию (последний / первый действующий) фиксируем в URL — ссылкой
  // на дашборд объекта можно поделиться.
  useEffect(() => {
    if (selected) {
      writeLastObject(selected);
    }
    if (selected && selected !== urlObject) {
      setParams({ object: selected }, { replace: true });
    }
  }, [selected, urlObject, setParams]);

  const dashQ = useGetProgressObjectDashboardQuery(selected ?? '', {
    skip: !can_read || !selected,
  });
  // `data` хранит последний ответ и при смене объекта — до прихода нового.
  const dash = dashQ.data;
  const stale = !!dash && dash.object.id !== selected;

  if (!can_read) {
    return (
      <FallbackBanner tone="danger" title="Доступ запрещён">
        У вас нет прав на раздел «Руководитель».
      </FallbackBanner>
    );
  }

  function choose(id: string) {
    writeLastObject(id);
    setParams({ object: id });
  }

  const current = objects.find((o) => o.id === selected);

  /** Кнопка «Поручение» в шапке блока — только с правом записи. */
  function instructButton(topic: InstructionTopic) {
    if (!can_write) return undefined;
    return (
      <Button
        type="button"
        $variant="ghost"
        $size="sm"
        onClick={() => setInstructTopic(topic)}
        title="Выдать поручение по этому показателю"
      >
        Поручение
      </Button>
    );
  }

  return (
    <Page>
      {/* Люди на объекте — выше всего: площадки берутся из Хронума целиком,
          поэтому баннер не зависит от того, какой объект ОСНОВЫ выбран. */}
      <ChronumBanner projectId={selected ?? undefined} />

      <div>
        <PickerLabel>Объект</PickerLabel>
        {objectsQ.isLoading ? (
          <Empty>Загрузка объектов…</Empty>
        ) : objects.length === 0 ? (
          <Empty>
            Объектов пока нет. Объекты заводятся в ОСНОВА.Инженер — после этого здесь появятся
            показатели по каждому.
          </Empty>
        ) : (
          <Chips role="tablist" aria-label="Объекты">
            {objects.map((o, i) => (
              <ObjectChip
                key={o.id}
                type="button"
                role="tab"
                aria-selected={o.id === selected}
                $active={o.id === selected}
                $index={i}
                onClick={() => choose(o.id)}
              >
                <b>{o.name}</b>
                <small>
                  {[o.code, o.customer_name].filter(Boolean).join(' · ') ||
                    (o.status === 'active' ? 'действующий' : 'в архиве')}
                </small>
              </ObjectChip>
            ))}
          </Chips>
        )}
      </div>

      {selected && (
        <>
          <ObjectHead>
            <div>
              <ObjectTitle>
                {(stale ? current?.name : (dash?.object.name ?? current?.name)) ?? '…'}
              </ObjectTitle>
              <ObjectMeta>
                {stale ? (
                  <span>Собираю показатели объекта…</span>
                ) : (
                  <>
                    {dash?.profile.customer_name && (
                      <span>Заказчик: {dash.profile.customer_name}</span>
                    )}
                    {(dash?.contracts ?? []).map((c) => (
                      <Badge key={c} $tone="neutral">
                        {c}
                      </Badge>
                    ))}
                    {dash && !dash.profile.saved && dash.profile.auto_filled && (
                      <Badge
                        $tone="info"
                        title="Заказчик, домены почты и маркеры — по ПТО и Диспетчеру"
                      >
                        привязки подобраны автоматически
                      </Badge>
                    )}
                    {dash && !dash.profile.saved && !dash.profile.auto_filled && (
                      <Badge $tone="warning">привязки не найдены</Badge>
                    )}
                    {dash && <span>обновлено {fmtDateTime(dash.generated_at)}</span>}
                  </>
                )}
              </ObjectMeta>
            </div>
            <HeadButtons>
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                onClick={() => setProfileOpen(true)}
              >
                <IconPencil size={14} />
                Привязки объекта
              </Button>
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                onClick={() => navigate(`/agents/progress/reports?project=${selected}`)}
              >
                <IconClipboardCheck size={14} />
                Сводный отчёт
              </Button>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => dashQ.refetch()}
                disabled={dashQ.isFetching}
                aria-label="Обновить показатели"
              >
                <IconRefresh size={14} />
                {dashQ.isFetching ? 'Обновляю…' : 'Обновить'}
              </Button>
            </HeadButtons>
          </ObjectHead>

          {dashQ.isError ? (
            <FallbackBanner tone="danger" title="Не удалось собрать показатели">
              {apiErrorMessage(dashQ.error) ??
                'Попробуйте обновить страницу; если ошибка повторяется — сообщите администратору.'}
            </FallbackBanner>
          ) : !dash ? (
            <Empty>Собираю показатели объекта…</Empty>
          ) : (
            <Content $stale={stale} aria-busy={stale}>
              {dash.warnings.length > 0 && (
                <Warnings aria-label="Чего не хватает для полной картины">
                  {dash.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </Warnings>
              )}
              <Grid>
                <ProgressCard
                  data={dash.progress}
                  projectId={dash.object.id}
                  delay={0}
                  actions={instructButton('progress')}
                />
                <BillingCard
                  data={dash.billing}
                  units={dash.progress.units}
                  delay={60}
                  actions={instructButton('billing')}
                />
                <CorrespondenceCard
                  data={dash.correspondence}
                  delay={120}
                  actions={instructButton('correspondence')}
                />
                <ClaimsCard data={dash.claims} delay={180} actions={instructButton('claims')} />
              </Grid>
              <Stack>
                <ObjectEconomyCard projectId={dash.object.id} objectName={dash.object.name} />
                <KspCard data={dash.ksp} delay={240} actions={instructButton('ksp')} />
                <OtherCard data={dash.other} delay={300} actions={instructButton('other')} />
              </Stack>
            </Content>
          )}
        </>
      )}

      {instructTopic && selected && dash && dash.object.id === selected && (
        <InstructionModal
          projectId={selected}
          objectName={dash.object.name}
          topic={instructTopic}
          suggestedText={suggestInstructionText(instructTopic, dash)}
          onClose={() => setInstructTopic(null)}
        />
      )}

      {profileOpen && selected && (
        <ObjectProfileModal
          projectId={selected}
          objectName={dash?.object.name ?? current?.name ?? ''}
          canWrite={can_write}
          onClose={() => setProfileOpen(false)}
        />
      )}
    </Page>
  );
}
